import type { FieldValues, Path, UseFormSetError } from "react-hook-form";

import { NETWORK_ERROR, isApiError } from "@/shared/api/errors";
import type { MessageId } from "@/shared/i18n/messages";

/** Server field names (as in the error envelope, such as "name" or "members.0.email") to form fields. */
export type ServerFieldMap<T extends FieldValues> = Readonly<Partial<Record<string, Path<T>>>>;

/* Messages for failures that belong to the whole form rather than one field. */
const ROOT_MESSAGES: Readonly<Partial<Record<string, MessageId>>> = {
  [NETWORK_ERROR]: "errors.network",
  PERMISSION_DENIED: "errors.forbidden",
  VERSION_CONFLICT: "errors.conflict",
};

function rootMessage(error: unknown, hasUnplacedFieldErrors: boolean): MessageId {
  if (hasUnplacedFieldErrors) return "errors.validation";
  return (isApiError(error) ? ROOT_MESSAGES[error.code] : undefined) ?? "errors.generic";
}

/**
 * Show a failed submission on the form. Like the form's own validation messages, every message
 * set here is a catalog id: the server's texts are English, so they are not shown as such.
 *
 * - Each server field error lands on its form field (through `fieldMap` when given, else by the
 *   same name) as "errors.invalidValue".
 * - Everything else lands on `errors.root`, which FormError shows: field errors that have no
 *   field in `fieldMap`, and failures that are not about fields (network, permission, conflict).
 *
 * Map the error codes a form knows better (such as PROJECT_KEY_TAKEN) before calling this.
 */
export function applyServerErrors<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
  fieldMap?: ServerFieldMap<T>,
): void {
  const serverFields = isApiError(error) ? Object.keys(error.fieldErrors) : [];
  let unplaced = false;
  for (const serverField of serverFields) {
    const field = fieldMap ? fieldMap[serverField] : (serverField as Path<T>);
    if (field === undefined) {
      unplaced = true;
    } else {
      setError(field, { type: "server", message: "errors.invalidValue" });
    }
  }
  if (serverFields.length > 0 && !unplaced) return;
  setError("root", { type: "server", message: rootMessage(error, unplaced) });
}
